import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'motion/react';
import { Button } from 'components/atoms/Button';
import useGetCardsList from 'hooks/Bank/useGetCardsList';
import { OnboardingStepWrapper } from 'components/organisms/OnboardingStepLayout';
import type { ActivationStepProps } from '../../types';
import ExpenseBadge from './ExpenseBadge';
import { BADGE_CONFIGS, POSITION_STYLES } from './badgeConfig';
// Fallback card image if API data is not available
import cardImg from 'assets/images/new_accountable_card.svg';

/**
 * Extended props for IssueCardStep
 * Includes skip callback for users who already have a card
 */
interface IssueCardStepProps extends ActivationStepProps {
  /** Callback when user has existing card - skips to YourCardReady */
  onSkipToCardReady?: () => void;
}

/**
 * Step to issue a card
 * Shows card animation with expense badges and allows user to continue or skip
 * Bookmark step for tracking
 *
 * This component fetches its own card data via useGetCardsList hook.
 * When user clicks "Get my card":
 * - If user already has an enabled card → calls onSkipToCardReady (skips to YourCardReady)
 * - Otherwise → calls onContinue (proceeds to CardDesign)
 */
const IssueCardStep: FC<IssueCardStepProps> = ({
  onContinue,
  onBack,
  onSkip,
  onSkipToCardReady,
  // Wrapper props (automatically injected)
  progressSegments,
  onProgressSegmentClick,
  title,
  subtitle,
  icon,
  showProgressBar,
  isUpdatingStep,
}) => {
  const { t, i18n } = useTranslation();

  // Fetch cards data - component owns its own data
  const { data: cards } = useGetCardsList();

  // Check if user already has an enabled card
  const hasEnabledCard = cards?.some((card) => card?.status === 'Enabled');

  // Get badge configuration for current language
  const badges = BADGE_CONFIGS[i18n.language] || BADGE_CONFIGS.en;

  return (
    <OnboardingStepWrapper
      showProgressBar={showProgressBar}
      progressSegments={progressSegments}
      onProgressSegmentClick={onProgressSegmentClick}
      title={title}
      subtitle={subtitle}
      icon={icon}
      showBackButton
      onBack={onBack}
    >
      <div className="flex justify-center mb-20 mt-12">
        <motion.div
          className="relative"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6 }}
        >
          {/* Card image at center */}
          <div className="relative w-[220px]">
            <img
              src={cardImg}
              alt="Accountable Aldo Card"
              className="w-full"
              style={{ aspectRatio: '5/3' }}
            />
          </div>

          {/* React-based expense badges around the card */}
          {badges.map((badge) => (
            <div key={badge.type} className={POSITION_STYLES[badge.position]}>
              <ExpenseBadge
                type={badge.type}
                title={t(badge.titleKey)}
                amount={badge.amount}
                delay={badge.delay}
              />
            </div>
          ))}
        </motion.div>
      </div>

      <div className="space-y-3">
        <Button
          onClick={() => {
            // If user already has an enabled card, skip to YourCardReady
            // Otherwise, proceed to CardDesign
            if (hasEnabledCard && onSkipToCardReady) {
              onSkipToCardReady();
            } else {
              onContinue();
            }
          }}
          className="w-full"
          color="bank"
          disabled={isUpdatingStep}
        >
          {t('accountable_banking.activation.issue_card.get_card_cta')}
        </Button>

        {onSkip && (
          <Button
            onClick={onSkip}
            className="w-full"
            structure="text"
            color="bank"
            disabled={isUpdatingStep}
          >
            {t('accountable_banking.activation.issue_card.skip_cta')}
          </Button>
        )}
      </div>
    </OnboardingStepWrapper>
  );
};

export default IssueCardStep;
